import { inferRouteObject } from 'react-router-typed-object';

import { generatePath, Navigate } from '@/js/libs/router';
import { PrivateRoute, Roles, or, and, not } from '@/js/libs/use-auth';

import type { Condition } from 'src/components/RoleBasedRedirector.tsx';
import RoleBasedRedirector from 'src/components/RoleBasedRedirector.tsx';
import RouteParamsValidator from 'src/components/RouteParamsValidator.tsx';
import { BroadcastRoleChecker } from 'src/scenes/Settings/containers/BroadcastProducts/components/BroadcastRoleChecker.tsx';
import { validateBroadcastType } from 'src/scenes/Settings/containers/BroadcastProducts/helpers.ts';
import Settings from 'src/scenes/Settings/index.ts';
import Status from 'src/scenes/Status/index.ts';

const index = true;

const companySettingsConditions: Condition[] = [
  { roles: Roles.POSTADMANAGER, to: generatePath('/settings/company-settings/pdf-type-config') },
  { roles: not(Roles.POSTADMANAGER), to: generatePath('/settings/company-settings/emails') },
];

const xandrProgrammaticConditions: Condition[] = [
  { roles: Roles.ADMIN, to: generatePath('/settings/xandr-programmatic/overview') },
  { roles: not(Roles.ADMIN), to: generatePath('/settings/xandr-programmatic/brands-mapping') },
];

const googleProgrammaticConditions: Condition[] = [
  { roles: Roles.ADMIN, to: generatePath('/settings/google-programmatic/websites-mapping') },
  { roles: not(Roles.ADMIN), to: generatePath('/settings/google-programmatic/brands-mapping') },
];

const adomikProgrammaticConditions: Condition[] = [
  { roles: Roles.ADMIN, to: generatePath('/settings/adomik-programmatic/websites-mapping') },
  { roles: not(Roles.ADMIN), to: generatePath('/settings/adomik-programmatic/brands-mapping') },
];

export const settingsRouteRoles = and(
  or(
    and(Roles.REPORT_BUILDER, or(Roles.BOOK_CAMPAIGNS, Roles.SALESFUNNEL, Roles.POSTADMANAGER)),
    Roles.ADMIN,
    Roles.POSTNETZ,
    Roles.POSTADMANAGER,
    Roles.MANAGE_ACCOUNTS,
    Roles.PAYOUT,
    Roles.MANAGE_DIGITAL_PRODUCTS,
    Roles.MANAGE_RADIO_PRODUCTS,
    Roles.MANAGE_TV_PRODUCTS,
    Roles.MANAGE_PRINT_PRODUCTS,
    Roles.MANAGE_POS_PRODUCTS,
    Roles.MANAGE_CONSULTING_PRODUCTS,
    Roles.BOOK_CAMPAIGNS,
    Roles.MANAGE_CAMPAIGN_TYPES,
    Roles.MANAGE_REFERENCE_PER_AD_MEDIA,
    Roles.CONTRACT,
  ),
  not(or(Roles.POSTNETZ_BRANCH_USER, Roles.READ_ONLY_POSTNETZ_BRANCH_USER)),
);

export const settingsRouteObject = inferRouteObject({
  path: 'settings',
  element: <PrivateRoute roles={settingsRouteRoles} noAccessComponent={Status.NotFound} />,
  children: [
    {
      Component: Settings.Helmet,
      children: [
        {
          index,
          element: <Settings.RoleBasedRedirector noMatchElement={<Status.NotFound />} />,
        },
        {
          element: <PrivateRoute roles={and(Roles.MANAGE_ACCOUNTS, not(Roles.POSTADMANAGER))} noAccessComponent={Status.NotFound} />,
          children: [
            {
              path: 'data-import',
              Component: Settings.DataImport,
            },
          ],
        },
        {
          path: 'digital-products',
          element: <PrivateRoute roles={Roles.MANAGE_DIGITAL_PRODUCTS} noAccessComponent={Status.NotFound} />,
          children: [
            { path: 'item/:id?', Component: Settings.DigitalProducts.Products.Edit },
            { path: 'history/:entityId', Component: Settings.DigitalProducts.Products.History },
            { path: 'ad-media/:mode/:id?', Component: Settings.DigitalProducts.AdMediaForm },
            { path: 'ad-media/history/:entityId', Component: Settings.DigitalProducts.AdMediaHistory },
            { path: 'price/:id?', Component: Settings.DigitalProducts.AdPrice.Edit },
            { path: 'price/history/:entityId', Component: Settings.DigitalProducts.AdPrice.History },
            { path: 'ad-type/:id?', Component: Settings.DigitalProducts.AdTypes.Edit },
            { path: 'ad-type/history/:entityId', Component: Settings.DigitalProducts.AdTypes.History },
            { path: 'placement/:id?', Component: Settings.DigitalProducts.Placement.Edit },
            { path: 'placement/history/:entityId', Component: Settings.DigitalProducts.Placement.History },
            {
              Component: Settings.DigitalProducts.Layout,
              children: [
                { index, Component: Settings.DigitalProducts.Products.List },
                { path: 'ad-media', Component: Settings.DigitalProducts.AdMediaList },
                { path: 'placements', Component: Settings.DigitalProducts.Placement.List },
                { path: 'ad-types', Component: Settings.DigitalProducts.AdTypes.List },
                { path: 'prices', Component: Settings.DigitalProducts.AdPrice.List },
                {
                  path: 'admedium-mapping',
                  children: [
                    { index, Component: Settings.DigitalProducts.AdMediumMapping.Products },
                    {
                      path: 'edit-mapping',
                      Component: Settings.DigitalProducts.AdMediumMapping.Mappings,
                    },
                  ],
                },
              ],
            },
          ],
        },
        {
          path: ':broadcastType',
          element: <RouteParamsValidator validate={validateBroadcastType} invalidStateElement={<Status.NotFound />} />,
          children: [
            {
              Component: BroadcastRoleChecker,
              children: [
                { index, Component: Settings.BroadcastProducts.Products },
                { path: 'create', Component: Settings.BroadcastProducts.ProductForm },
                { path: 'edit/:productId', Component: Settings.BroadcastProducts.ProductForm },
                { path: 'history/:entityId', Component: Settings.BroadcastProducts.ProductHistory },

                {
                  path: 'ad-media',
                  children: [
                    { index, Component: Settings.BroadcastProducts.AdMedia },
                    { path: 'history/:entityId', Component: Settings.BroadcastProducts.AdMediaHistory },
                  ],
                },
                {
                  path: 'placements',
                  children: [
                    { index, Component: Settings.BroadcastProducts.AdPlacements },
                    { path: 'create', Component: Settings.BroadcastProducts.AdPlacementForm },
                    { path: 'edit/:placementId', Component: Settings.BroadcastProducts.AdPlacementForm },
                    { path: 'history/:entityId', Component: Settings.BroadcastProducts.AdPlacementHistory },
                  ],
                },

                {
                  path: 'ad-types',
                  children: [
                    { index, Component: Settings.BroadcastProducts.AdTypes },
                    { path: 'history/:entityId', Component: Settings.BroadcastProducts.AdTypeHistory },
                  ],
                },
                {
                  path: 'ad-prices',
                  children: [
                    { index, Component: Settings.BroadcastProducts.AdPrices },
                    { path: 'create', Component: Settings.BroadcastProducts.CreateAdPriceForm },
                    { path: 'edit/:priceId', Component: Settings.BroadcastProducts.EditAdPriceForm },
                    { path: 'history/:entityId', Component: Settings.BroadcastProducts.AdPriceHistory },
                  ],
                },
              ],
            },
          ],
        },
        {
          element: <PrivateRoute roles={Roles.BOOK_CAMPAIGNS} noAccessComponent={Status.NotFound} />,
          children: [
            {
              path: 'sales-units',
              Component: Settings.SalesUnits,
            },
          ],
        },
        {
          element: <PrivateRoute roles={Roles.PAYOUT} noAccessComponent={Status.NotFound} />,
          children: [
            {
              path: 'websites',
              children: [
                { index, Component: Settings.Websites.WebsitesList },
                { path: 'create', Component: Settings.Websites.WebsiteForm },
                {
                  path: ':websiteId',
                  children: [
                    { index, Component: Settings.Websites.WebsiteForm },
                    { path: 'history', Component: Settings.Websites.WebsiteHistory },
                  ],
                },
              ],
            },
          ],
        },
        {
          path: 'company-settings',
          element: <PrivateRoute roles={or(Roles.BOOK_CAMPAIGNS, Roles.POSTADMANAGER)} noAccessComponent={Status.NotFound} />,
          children: [
            {
              index,
              element: <RoleBasedRedirector conditions={companySettingsConditions} noAccessElement={<Status.NotFound />} />,
            },
            {
              Component: Settings.CompanySettings.SalesUnitsProvider,
              children: [
                // Routes accessible only to non-POSTADMANAGER users
                {
                  element: <PrivateRoute roles={not(Roles.POSTADMANAGER)} noAccessComponent={Status.NotFound} />,
                  children: [
                    // Pages inside CompanySettings.Layout
                    {
                      Component: Settings.CompanySettings.Layout,
                      children: [
                        {
                          path: 'pdf-footer',
                          Component: Settings.CompanySettings.PDFFooterTemplates,
                        },
                        {
                          path: 'payment-details',
                          element: <PrivateRoute roles={Roles.ADMIN_INVOICE} noAccessComponent={Status.NotFound} />,
                          children: [{ index, Component: Settings.CompanySettings.PaymentDetails }],
                        },
                      ],
                    },
                    // Pages not in CompanySettings.Layout
                    {
                      path: 'pdf-styles',
                      children: [
                        {
                          index,
                          Component: Settings.CompanySettings.PDFStyles,
                        },
                        {
                          path: 'edit',
                          Component: Settings.CompanySettings.PDFStylesForm,
                          searchParams: Settings.CompanySettings.PDFStylesFormSearchParams.parse,
                        },
                      ],
                    },
                    {
                      path: 'emails',
                      children: [
                        {
                          index,
                          Component: Settings.CompanySettings.EmailTemplates,
                        },
                        {
                          path: 'create',
                          Component: Settings.CompanySettings.EmailTemplateForm,
                          searchParams: Settings.CompanySettings.EmailTemplateFormSearchParams.parse,
                        },
                        {
                          path: ':emailTemplateId',
                          Component: Settings.CompanySettings.EmailTemplateForm,
                        },
                      ],
                    },
                    {
                      path: 'email-designs',
                      children: [
                        {
                          index,
                          Component: Settings.CompanySettings.EmailDesigns,
                        },
                        {
                          path: 'create',
                          Component: Settings.CompanySettings.EmailDesignForm,
                          searchParams: Settings.CompanySettings.EmailDesignFormSearchParams.parse,
                        },
                        {
                          path: ':emailDesignId',
                          Component: Settings.CompanySettings.EmailDesignForm,
                          searchParams: Settings.CompanySettings.EmailDesignFormSearchParams.parse,
                        },
                      ],
                    },
                    {
                      path: 'payment-details',
                      element: <PrivateRoute roles={Roles.ADMIN_INVOICE} noAccessComponent={Status.NotFound} />,
                      children: [
                        {
                          path: 'create',
                          children: [
                            { index, element: <Navigate to='../' replace /> },
                            { path: ':salesUnitId', Component: Settings.CompanySettings.PaymentDetailsForm },
                          ],
                        },
                        {
                          path: 'edit',
                          children: [
                            { index, element: <Navigate to='../' replace /> },
                            { path: ':invoiceInfoId', Component: Settings.CompanySettings.PaymentDetailsForm },
                          ],
                        },
                      ],
                    },
                    {
                      path: 'sequencing',
                      Component: Settings.CompanySettings.Sequencing,
                    },
                    {
                      path: 'coop-weeks',
                      element: <PrivateRoute roles={Roles.MANAGE_COOP_WEEK} noAccessComponent={Status.NotFound} />,
                      children: [{ index, Component: Settings.CompanySettings.CoopWeeks }],
                    },
                    {
                      path: 'ppt-report-config',
                      Component: Settings.CompanySettings.PPTReportConfig,
                    },
                    {
                      path: 'user-id-modules',
                      element: <PrivateRoute roles={or(Roles.ADMIN, Roles.HEADER_TAG)} noAccessComponent={Status.NotFound} />,
                      children: [{ index, Component: Settings.CompanySettings.UserIdModules }],
                    },
                  ],
                },
                // Routes accessible to all (including POSTADMANAGER)
                {
                  path: 'pdf-type-config',
                  Component: Settings.CompanySettings.PDFTypeConfigurations,
                },
              ],
            },
          ],
        },
        {
          path: 'adsense',
          element: <PrivateRoute roles={Roles.ADMIN} noAccessComponent={Status.NotFound} />,
          children: [
            {
              Component: Settings.AdSenseSettings.Layout,
              children: [
                { index, element: <Navigate to='./mappings' replace /> },
                { path: 'mappings', Component: Settings.AdSenseSettings.Mappings },
                {
                  path: 'credentials',
                  children: [
                    { index, Component: Settings.AdSenseSettings.Credentials },
                    { path: 'oauth2callback', Component: Settings.AdSenseSettings.OauthCallback },
                  ],
                },
              ],
            },
          ],
        },
        {
          path: 'xandr-programmatic',
          element: <PrivateRoute roles={or(Roles.ADMIN, Roles.PAYOUT)} noAccessComponent={Status.NotFound} />,
          children: [
            {
              Component: Settings.XandrProgrammatic.Layout,
              children: [
                {
                  index,
                  element: <RoleBasedRedirector conditions={xandrProgrammaticConditions} noAccessElement={<Status.NotFound />} />,
                },
                {
                  element: <PrivateRoute roles={Roles.ADMIN} noAccessComponent={Status.NotFound} />,
                  children: [
                    { index, Component: Settings.XandrProgrammatic.OverviewRedirect },
                    {
                      path: 'overview',
                      children: [
                        { index, Component: Settings.XandrProgrammatic.OverviewRedirect },
                        { path: ':date', Component: Settings.XandrProgrammatic.Overview },
                      ],
                    },
                    { path: 'websites-mapping', Component: Settings.XandrProgrammatic.WebsitesMapping },
                    { path: 'deals', Component: Settings.XandrProgrammatic.Deals },
                  ],
                },
                {
                  element: <PrivateRoute roles={Roles.PAYOUT} noAccessComponent={Status.NotFound} />,
                  children: [
                    { path: 'brands-mapping', Component: Settings.XandrProgrammatic.BrandsMapping },
                    { path: 'buyer-mapping', Component: Settings.XandrProgrammatic.BuyerMapping },
                  ],
                },
              ],
            },
          ],
        },
        {
          path: 'google-programmatic',
          element: <PrivateRoute roles={or(Roles.ADMIN, Roles.PAYOUT)} noAccessComponent={Status.NotFound} />,
          children: [
            {
              Component: Settings.GoogleProgrammatic.Layout,
              children: [
                {
                  index,
                  element: <RoleBasedRedirector conditions={googleProgrammaticConditions} noAccessElement={<Status.NotFound />} />,
                },
                {
                  element: <PrivateRoute roles={Roles.ADMIN} noAccessComponent={Status.NotFound} />,
                  children: [{ path: 'websites-mapping', Component: Settings.GoogleProgrammatic.WebsitesMapping }],
                },
                {
                  element: <PrivateRoute roles={Roles.PAYOUT} noAccessComponent={Status.NotFound} />,
                  children: [
                    { path: 'brands-mapping', Component: Settings.GoogleProgrammatic.BrandsMapping },
                    { path: 'buyer-mapping', Component: Settings.GoogleProgrammatic.BuyerMapping },
                  ],
                },
              ],
            },
          ],
        },
        {
          path: 'adomik-programmatic',
          element: <PrivateRoute roles={or(Roles.ADMIN, Roles.PAYOUT)} noAccessComponent={Status.NotFound} />,
          children: [
            {
              Component: Settings.AdomikProgrammatic.Layout,
              children: [
                {
                  index,
                  element: <RoleBasedRedirector conditions={adomikProgrammaticConditions} noAccessElement={<Status.NotFound />} />,
                },
                {
                  element: <PrivateRoute roles={Roles.ADMIN} noAccessComponent={Status.NotFound} />,
                  children: [
                    { path: 'websites-mapping', Component: Settings.AdomikProgrammatic.WebsiteMappings },
                    { path: 'deals-dbe', Component: Settings.AdomikProgrammatic.Deals },
                    { path: 'seats', Component: Settings.AdomikProgrammatic.Seats },
                  ],
                },
                {
                  element: <PrivateRoute roles={Roles.PAYOUT} noAccessComponent={Status.NotFound} />,
                  children: [
                    { path: 'brands-mapping', Component: Settings.AdomikProgrammatic.BrandMappings },
                    { path: 'buyer-mapping', Component: Settings.AdomikProgrammatic.BuyerMappings },
                    { path: 'ssp-mapping', Component: Settings.AdomikProgrammatic.SSPMappings },
                  ],
                },
              ],
            },
          ],
        },
        {
          path: 'passendo',
          element: <PrivateRoute roles={Roles.ADMIN} noAccessComponent={Status.NotFound} />,
          children: [
            {
              Component: Settings.PassendoProgrammatic.Layout,

              children: [
                { path: 'websites-mapping', Component: Settings.PassendoProgrammatic.WebsitesMapping },
                { index, element: <Navigate to='./websites-mapping' replace /> },
              ],
            },
          ],
        },
        {
          path: 'brands',
          element: <PrivateRoute roles={or(Roles.ADMIN, Roles.PAYOUT)} noAccessComponent={Status.NotFound} />,
          children: [
            {
              Component: Settings.Brands.Layout,
              children: [
                { index, Component: Settings.Brands.List },
                { path: 'websites', Component: Settings.Brands.Websites },
              ],
            },
          ],
        },
        {
          element: (
            <PrivateRoute
              roles={and(Roles.REPORT_BUILDER, or(Roles.BOOK_CAMPAIGNS, Roles.SALESFUNNEL, Roles.POSTADMANAGER))}
              noAccessComponent={Status.NotFound}
            />
          ),
          children: [{ path: 'labels', Component: Settings.Labels }],
        },
        {
          element: <PrivateRoute roles={Roles.MANAGE_CAMPAIGN_TYPES} noAccessComponent={Status.NotFound} />,
          children: [
            { path: 'campaign-types', Component: Settings.CampaignTypes },
            { path: 'campaign-types-v2', Component: Settings.CampaignTypesV2 },
          ],
        },
        {
          element: <PrivateRoute roles={or(Roles.MANAGE_ACCOUNTS, Roles.POSTADMANAGER)} noAccessComponent={Status.NotFound} />,
          children: [{ path: 'industries', Component: Settings.Industries }],
        },
        {
          element: <PrivateRoute roles={Roles.MANAGE_ACCOUNTS} noAccessComponent={Status.NotFound} />,
          children: [{ path: 'account-types', Component: Settings.AccountTypes }],
        },

        {
          path: 'position-groups',
          element: (
            <PrivateRoute
              roles={or(
                Roles.MANAGE_DIGITAL_PRODUCTS,
                Roles.MANAGE_PRINT_PRODUCTS,
                Roles.MANAGE_POS_PRODUCTS,
                Roles.MANAGE_CONSULTING_PRODUCTS,
              )}
              noAccessComponent={Status.NotFound}
            />
          ),
          children: [
            { index, Component: Settings.PositionGroups.PositionGroupsList },
            { path: 'create', Component: Settings.PositionGroups.PositionGroupForm },
            { path: 'edit/:positionGroupId', Component: Settings.PositionGroups.PositionGroupForm },
            { path: 'edit-positions/:positionGroupId', Component: Settings.PositionGroups.PositionsForm },
          ],
        },
        {
          path: 'print-products',
          element: <PrivateRoute roles={Roles.MANAGE_PRINT_PRODUCTS} noAccessComponent={Status.NotFound} />,
          children: [
            { path: 'placements/:itemId', Component: Settings.PrintProducts.Placements.Form },
            { path: 'placements/create', Component: Settings.PrintProducts.Placements.Form },
            { path: 'placements/history/:entityId', Component: Settings.PrintProducts.Placements.PlacementHistory },
            { path: ':itemId', Component: Settings.PrintProducts.Products.Form },
            { path: 'create', Component: Settings.PrintProducts.Products.Form },
            { path: 'history/:entityId', Component: Settings.PrintProducts.Products.ProductHistory },
            { path: 'ad-prices/:itemId', Component: Settings.PrintProducts.AdPrices.Form },
            { path: 'ad-prices/create', Component: Settings.PrintProducts.AdPrices.Form },
            { path: 'ad-prices/history/:entityId', Component: Settings.PrintProducts.AdPrices.AdPriceHistory },
            { path: 'calendars/:itemId', Component: Settings.PrintProducts.Calendars.Form },
            { path: 'calendars/create', Component: Settings.PrintProducts.Calendars.Form },
            { path: 'ad-types/history/:entityId', Component: Settings.PrintProducts.AdTypes.AdTypeHistory },
            { path: 'ad-medias/history/:entityId', Component: Settings.PrintProducts.AdMedias.AdMediaHistory },
            {
              Component: Settings.PrintProducts.Layout,
              children: [
                { index, Component: Settings.PrintProducts.Products.List },
                { path: 'ad-medias', Component: Settings.PrintProducts.AdMedias.List },
                { path: 'placements', Component: Settings.PrintProducts.Placements.List },
                { path: 'ad-types', Component: Settings.PrintProducts.AdTypes.List },
                { path: 'ad-prices', Component: Settings.PrintProducts.AdPrices.List },
                { path: 'calendars', Component: Settings.PrintProducts.Calendars.List },
                { path: 'size-configs', Component: Settings.PrintProducts.SizeConfigs.List },
                { path: 'availabilities', Component: Settings.PrintProducts.Availabilities.List },
              ],
            },
          ],
        },
        {
          path: 'point-of-sale-products',
          element: <PrivateRoute roles={Roles.MANAGE_POS_PRODUCTS} noAccessComponent={Status.NotFound} />,
          children: [
            { path: 'create', Component: Settings.PointOfSaleProducts.ProductForm },
            {
              path: 'edit',
              children: [
                { index, element: <Navigate to='../' replace /> },
                { path: ':id', Component: Settings.PointOfSaleProducts.ProductForm },
              ],
            },
            { path: 'ad-prices/create', Component: Settings.PointOfSaleProducts.AdPriceForm },
            {
              path: 'ad-prices/edit',
              children: [
                { index, element: <Navigate to='../' replace /> },
                { path: ':id', Component: Settings.PointOfSaleProducts.AdPriceForm },
              ],
            },
            { path: 'history/:entityId', Component: Settings.PointOfSaleProducts.ProductHistory },
            { path: 'ad-media/history/:entityId', Component: Settings.PointOfSaleProducts.AdMediaHistory },
            { path: 'placements/history/:entityId', Component: Settings.PointOfSaleProducts.PlacementHistory },
            { path: 'ad-types/history/:entityId', Component: Settings.PointOfSaleProducts.AdTypeHistory },
            { path: 'ad-prices/history/:entityId', Component: Settings.PointOfSaleProducts.AdPriceHistory },
            {
              Component: Settings.PointOfSaleProducts.Layout,
              children: [
                { index, Component: Settings.PointOfSaleProducts.Products },
                { path: 'ad-media', Component: Settings.PointOfSaleProducts.AdMedia },
                { path: 'placements', Component: Settings.PointOfSaleProducts.Placements },
                { path: 'ad-types', Component: Settings.PointOfSaleProducts.AdTypes },
                { path: 'ad-prices', Component: Settings.PointOfSaleProducts.AdPrices },
              ],
            },
          ],
        },
        {
          path: 'consulting-products',
          element: <PrivateRoute roles={Roles.MANAGE_CONSULTING_PRODUCTS} noAccessComponent={Status.NotFound} />,
          children: [
            {
              Component: Settings.ConsultingProducts.Helmet,
              children: [
                { path: 'create', Component: Settings.ConsultingProducts.ProductForm },
                {
                  path: 'edit',
                  children: [
                    { index, element: <Navigate to='../' replace /> },
                    { path: ':id', Component: Settings.ConsultingProducts.ProductForm },
                  ],
                },
                { path: 'ad-prices/create', Component: Settings.ConsultingProducts.AdPriceForm },
                {
                  path: 'ad-prices/edit',
                  children: [
                    { index, element: <Navigate to='../' replace /> },
                    { path: ':id', Component: Settings.ConsultingProducts.AdPriceForm },
                  ],
                },
                { path: 'history/:entityId', Component: Settings.ConsultingProducts.ProductHistory },
                { path: 'ad-media/history/:entityId', Component: Settings.ConsultingProducts.AdMediaHistory },
                { path: 'placements/history/:entityId', Component: Settings.ConsultingProducts.PlacementHistory },
                { path: 'ad-types/history/:entityId', Component: Settings.ConsultingProducts.AdTypeHistory },
                { path: 'ad-prices/history/:entityId', Component: Settings.ConsultingProducts.AdPriceHistory },
                {
                  Component: Settings.ConsultingProducts.Layout,
                  children: [
                    { index, Component: Settings.ConsultingProducts.ProductsList },
                    { path: 'ad-media', Component: Settings.ConsultingProducts.AdMediaList },
                    { path: 'placements', Component: Settings.ConsultingProducts.PlacementsList },
                    { path: 'ad-types', Component: Settings.ConsultingProducts.AdTypesList },
                    { path: 'ad-prices', Component: Settings.ConsultingProducts.AdPricesList },
                  ],
                },
              ],
            },
          ],
        },
        {
          element: (
            <PrivateRoute
              roles={or(
                Roles.MANAGE_DIGITAL_PRODUCTS,
                Roles.MANAGE_PRINT_PRODUCTS,
                Roles.MANAGE_TV_PRODUCTS,
                Roles.MANAGE_RADIO_PRODUCTS,
                Roles.MANAGE_POS_PRODUCTS,
                Roles.MANAGE_CONSULTING_PRODUCTS,
              )}
              noAccessComponent={Status.NotFound}
            />
          ),
          children: [{ path: 'price-lists', Component: Settings.PriceLists }],
        },
        {
          path: 'analog-products',
          element: <PrivateRoute roles={Roles.POSTNETZ} noAccessComponent={Status.NotFound} />,
          children: [
            { index, element: <Navigate to='./advertisements' replace /> },
            {
              Component: Settings.AnalogProducts.Layout,
              children: [
                {
                  path: 'advertisements',
                  children: [
                    { path: ':advertisementId', Component: Settings.AnalogProducts.AdvertisementsEdit },
                    { index, Component: Settings.AnalogProducts.Advertisements },
                  ],
                },
                { path: 'advertising-spaces', Component: Settings.AnalogProducts.AdvertisingSpaces },
                { path: 'branch-cluster-factors', Component: Settings.AnalogProducts.BranchClusters },
                { path: 'branch-count-factors', Component: Settings.AnalogProducts.BranchCountFactors },
                { path: 'dispatches', Component: Settings.AnalogProducts.Dispatches },
                { path: 'monthly-factors', Component: Settings.AnalogProducts.MonthlyFactors },
              ],
            },
            {
              path: 'position-groups',
              children: [
                { index, Component: Settings.AnalogProducts.PositionGroups.List },
                { path: 'create', Component: Settings.AnalogProducts.PositionGroups.PositionGroupForm },
                {
                  path: ':groupId',
                  children: [
                    { index, Component: Settings.AnalogProducts.PositionGroups.PositionGroupForm },
                    { path: 'positions', Component: Settings.AnalogProducts.PositionGroups.PositionsForm },
                  ],
                },
              ],
            },
          ],
        },
        {
          path: 'partner-filial-management',
          children: [
            { index, element: <Navigate to='./partner-branches' replace /> },
            { path: 'partner-branches', Component: Settings.PartnerFilialManagement.PartnerBranches },
          ],
        },
        {
          path: 'reference-per-admedia',
          element: <PrivateRoute roles={Roles.MANAGE_REFERENCE_PER_AD_MEDIA} noAccessComponent={Status.NotFound} />,
          children: [
            { index, Component: Settings.ReferencePerAdmedia.List },
            { path: 'create', Component: Settings.ReferencePerAdmedia.Edit },
            { path: 'edit/:id', Component: Settings.ReferencePerAdmedia.Edit },
          ],
        },
        {
          path: 'special-discounts',
          element: <PrivateRoute roles={Roles.SPECIAL_DISCOUNTS} noAccessComponent={Status.NotFound} />,
          children: [{ index, Component: Settings.SpecialDiscounts }],
        },
        {
          path: 'contracts',
          element: <PrivateRoute roles={Roles.CONTRACT} noAccessComponent={Status.NotFound} />,
          children: [
            { index, Component: Settings.Contracts.ContractsList },
            { path: ':discountContractId/history', Component: Settings.Contracts.ContractHistory },
          ],
        },
      ],
    },
  ],
});
